import React, { useState, useEffect, useCallback, useMemo } from "react";
import { format, utcToZonedTime } from "date-fns-tz";
import LoadingSpinner from "../../../../components/core/LoadingSpinner";
import { useBookingData } from "../../../../providers/BookingDataProvider";
import { useTranslation } from "react-i18next";
import type { TimeSlot } from "../../../../types/booking";
import type { MeetingType } from "../../../../services/meetingTypeService";
import meetingTypeService from "../../../../services/meetingTypeService";

interface TimeSlotDisplayProps {
  selectedDate: Date | null;
  onTimeSelect: (slot: TimeSlot) => void;
  selectedTimezone: string;
  meetingType: MeetingType | null;
  selectedDuration?: number;
  onRefreshCapacityRequest?: () => void; // Callback to expose refresh method
  /** Real meeting type ID for preview mode (for fetching capacity data when meetingType.id is -1) */
  previewRealMeetingTypeId?: number | null;
  /** Event waitlist claim: the slot held for this guest (from a claim email link).
   *  That slot renders as "Reserved for you" and is bookable even though it reads Full. */
  claimedSlot?: { start: Date; end: Date } | null;
  /** Event waitlist: called when a guest picks a FULL slot on a waitlist-enabled
   *  event. The parent advances through the normal booking form in waitlist mode. */
  onWaitlistSelect?: (slot: TimeSlot) => void;
}

/**
 * TimeSlotDisplay Component - Unified with Provider Cache Management
 *
 * Features:
 * 1. Immediate cache check on render
 * 2. Relies on BookingDataProvider for timezone switching
 * 3. Single source of truth for slot display
 * 4. Clean separation of concerns
 */
const TimeSlotDisplay: React.FC<TimeSlotDisplayProps> = ({
  selectedDate,
  onTimeSelect,
  selectedTimezone,
  meetingType,
  selectedDuration,
  previewRealMeetingTypeId,
  claimedSlot,
  onWaitlistSelect,
}) => {
  const { loadingSlots, fetchAvailableSlots, getSlotsForDate } =
    useBookingData();
  const { t } = useTranslation();
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [shouldFetchSlots, setShouldFetchSlots] = useState<boolean>(false);

  // Event capacity state
  const [slotCapacities, setSlotCapacities] = useState<Record<string, { capacity: number; bookedCount: number; available: number }>>({});
  const [loadingCapacities, setLoadingCapacities] = useState<boolean>(false);

  // Real meeting type ID (preview mode uses -1 with previewRealMeetingTypeId)
  const effectiveMeetingTypeId = useMemo(() => {
    if (!meetingType) return null;
    return meetingType.id === -1 && previewRealMeetingTypeId
      ? previewRealMeetingTypeId
      : meetingType.id;
  }, [meetingType, previewRealMeetingTypeId]);

  // IMMEDIATE cache check with direct computation
  const displaySlots = useMemo(() => {
    if (!selectedDate || !meetingType) return [];

    // Check cache immediately and return processed slots
    const effectiveDuration =
      selectedDuration || meetingType.duration?.[0] || 30;

    // Get cached slots from provider
    const cachedSlots = getSlotsForDate(
      selectedDate,
      selectedTimezone,
      effectiveDuration
    );
    if (!cachedSlots || cachedSlots.length === 0) {
      return [];
    }

    // Process slots immediately - single computation, no state updates
    const processedSlots = cachedSlots
      .map((slot) => ({
        ...slot,
        start: slot.start instanceof Date ? slot.start : new Date(slot.start),
        end: slot.end instanceof Date ? slot.end : new Date(slot.end),
        displayTimezone: slot.displayTimezone || selectedTimezone,
        timezone: slot.timeZone || slot.apiTimezone || selectedTimezone,
        apiTimezone: slot.apiTimezone || slot.timeZone || selectedTimezone,
      }))
      .filter(
        (slot) => !isNaN(slot.start.getTime()) && !isNaN(slot.end.getTime())
      )
      .filter((slot) => {
        // Filter to selected date only
        if (!selectedDate) return true;
        const slotStartInTimezone = utcToZonedTime(
          slot.start,
          selectedTimezone
        );
        const selectedDateInTimezone = utcToZonedTime(
          selectedDate,
          selectedTimezone
        );
        const slotDate = format(slotStartInTimezone, "yyyy-MM-dd");
        const selectedDateString = format(selectedDateInTimezone, "yyyy-MM-dd");
        return slotDate === selectedDateString;
      })
      .sort((a, b) => a.start.getTime() - b.start.getTime());

    return processedSlots;
  }, [
    selectedDate,
    selectedTimezone,
    meetingType,
    selectedDuration,
    getSlotsForDate,
  ]);

  // Determine if we need to fetch (only when no cached slots available)
  const needsFetch = useMemo(() => {
    if (!selectedDate || !meetingType) return false;
    return displaySlots.length === 0 && !loadingSlots;
  }, [selectedDate, meetingType, displaySlots.length, loadingSlots]);

  // Trigger fetch when needed - simplified logic
  useEffect(() => {
    if (!needsFetch) return;
    setShouldFetchSlots(true);
  }, [needsFetch]);

  // Note: Timezone change handling is now managed by BookingDataProvider's switchTimezone method
  // No need for component-level timezone fetch triggers

  // Simplified fetch effect
  useEffect(() => {
    if (!shouldFetchSlots || !selectedDate || !meetingType) return;

    const fetchSlots = async () => {
      try {
        setFetchError(null);
        // Use the viewer's selected timezone — backend reads host timezone from DB
        const apiTimezone = selectedTimezone;

        // This will update the cache in BookingDataProvider
        await fetchAvailableSlots(
          selectedDate,
          apiTimezone,
          meetingType,
          selectedDuration
        );
      } catch (error: any) {
        setFetchError(
          error.message || "Failed to fetch available slots. Please try again."
        );
      } finally {
        setShouldFetchSlots(false);
      }
    };

    fetchSlots();
  }, [
    shouldFetchSlots,
    selectedDate,
    selectedTimezone,
    meetingType,
    selectedDuration,
    fetchAvailableSlots,
  ]);

  // Fetch capacity data for event meeting types
  const fetchCapacities = useCallback(async () => {
    if (!meetingType || meetingType.type !== 'event' || displaySlots.length === 0) {
      return;
    }

    // Skip capacity fetch if we don't have a valid meeting type ID
    // (e.g., creating a new meeting type that hasn't been saved yet)
    if (!effectiveMeetingTypeId || effectiveMeetingTypeId <= 0) {
      return;
    }

    setLoadingCapacities(true);
    try {
      const capacityPromises = displaySlots.map(async (slot) => {
        try {
          const slotKey = `${slot.start.toISOString()}-${slot.end.toISOString()}`;
          // Use the public API endpoint (no token required)
          const capacity = await meetingTypeService.getSlotCapacity(
            '', // No token needed for public endpoint
            effectiveMeetingTypeId,
            slot.start.toISOString(),
            slot.end.toISOString()
          );
          return { slotKey, capacity };
        } catch (error) {
          return null;
        }
      });

      const results = await Promise.all(capacityPromises);
      const capacityMap: Record<string, { capacity: number; bookedCount: number; available: number }> = {};

      results.forEach((result) => {
        if (result) {
          capacityMap[result.slotKey] = result.capacity;
        }
      });

      setSlotCapacities(capacityMap);
    } catch (error) {
    } finally {
      setLoadingCapacities(false);
    }
  }, [
    meetingType,
    meetingType?.id, // Refetch when meeting type ID changes
    meetingType?.maxCapacity, // Refetch when capacity settings change (critical for preview)
    meetingType?.showSlotsLeftEnabled, // Refetch when display toggle changes
    meetingType?.showSlotsLeftThreshold, // Refetch when threshold changes
    effectiveMeetingTypeId, // Refetch when real ID changes (for preview mode)
    displaySlots
  ]);

  // Auto-fetch capacities when slots change
  useEffect(() => {
    fetchCapacities();
  }, [fetchCapacities]);

  // Handle slot selection
  const handleSlotSelect = useCallback(
    (slot: TimeSlot) => {
      setSelectedSlot(slot);
      onTimeSelect(slot);
    },
    [onTimeSelect]
  );

  // Get slot key for comparison
  const getSlotKey = useCallback((startDate: Date, endDate: Date): string => {
    if (!startDate || !endDate) return "invalid-date";
    return `${startDate.toISOString()}-${endDate.toISOString()}`;
  }, []);

  // TimeSlotDisplay component ready to render

  // RENDER - Simple, direct, no flash possible
  return (
    <div className="grid grid-cols-1 gap-2 pb-4 max-h-96 overflow-y-auto">
      {loadingSlots && displaySlots.length === 0 ? (
        // Show loading only when actually loading AND no cached slots
        <div className="py-8 text-center">
          <LoadingSpinner size="lg" color="primary" className="inline-block mb-2" />
          <p className="text-gray-500">
            {t("booking:timeSlots.loadingSlots", "Loading available times...")}
          </p>
        </div>
      ) : fetchError ? (
        // Show error
        <div className="py-8 text-center">
          <p className="text-red-500">{fetchError}</p>
        </div>
      ) : displaySlots.length === 0 && !loadingSlots ? (
        // Show no slots message ONLY when not loading and truly no slots
        <div className="py-8 text-center">
          <p className="text-gray-500">
            {t("booking:timeSlots.noTimes", "No available times for this date")}
          </p>
        </div>
      ) : (
        // Render slots immediately
        displaySlots.map((slot, index) => {
          const slotKey = getSlotKey(slot.start, slot.end);

          // Format time for display
          let timeString;
          if (slot.formattedStart) {
            timeString = slot.formattedStart;
          } else {
            const displayTimezone = slot.displayTimezone || selectedTimezone;
            const zonedDate = utcToZonedTime(slot.start, displayTimezone);
            timeString = format(zonedDate, "HH:mm");
          }

          const isSelected =
            selectedSlot &&
            getSlotKey(selectedSlot.start, selectedSlot.end) === slotKey;

          // Generate the CSS classes (base styles only, colors via CSS variables)
          const buttonClasses = [
            "relative overflow-hidden",
            "p-3 text-sm font-medium rounded-lg border-2",
            "opacity-100 transform translate-y-0",
            "transition-all duration-200",
          ];

          if (isSelected) {
            buttonClasses.push("shadow-lg");
          } else {
            buttonClasses.push("bg-white hover:shadow-md");
          }

          // Use CSS variables for colors (supports dynamic meeting type colors)
          const dynamicStyles: React.CSSProperties = isSelected
            ? {
                backgroundColor: "var(--brand-color, #2E7AA0)",
                borderColor: "var(--brand-color, #2E7AA0)",
                color: "var(--brand-text-color, white)",
                minHeight: "48px",
              }
            : {
                borderColor: "var(--brand-color-very-light, #D1E9F5)",
                color: "var(--brand-color, #2E7AA0)",
                minHeight: "48px",
              };

          // Add hover styles via CSS variables for non-selected slots
          if (!isSelected) {
            Object.assign(dynamicStyles, {
              "--hover-bg": "var(--brand-color-very-light, #E8F4FA)",
              "--hover-border": "var(--brand-color-light, #A3D3EB)",
            } as any);
          }

          // Get capacity info for event types
          const capacityInfo = meetingType?.type === 'event' ? slotCapacities[slotKey] : null;
          const isFull = capacityInfo && capacityInfo.available === 0;

          // Event waitlist:
          // - claimed slot (guest arrived via a claim email): bookable even though Full
          // - full slot with waitlistEnabled: clickable → opens the join-waitlist modal
          const isClaimedSlot =
            !!claimedSlot && slot.start.getTime() === claimedSlot.start.getTime();
          const waitlistEnabled = meetingType?.waitlistEnabled ?? false;
          const isWaitlistSlot = !!isFull && waitlistEnabled && !isClaimedSlot;
          const isDisabled = !!isFull && !isWaitlistSlot && !isClaimedSlot;

          // Determine if we should show the capacity indicator
          // Show if: (1) it's an event type, (2) we have capacity info, (3) feature is enabled, and (4) available <= threshold
          const showSlotsLeftEnabled = meetingType?.showSlotsLeftEnabled ?? false;
          const showSlotsLeftThreshold = meetingType?.showSlotsLeftThreshold ?? 10;
          const shouldShowCapacity = capacityInfo && showSlotsLeftEnabled && capacityInfo.available <= showSlotsLeftThreshold;
          const showCapacity = !!shouldShowCapacity || isFull || isClaimedSlot; // Always show if full/claimed, otherwise check threshold

          // Waitlist slots are deliberately muted (light grey) so they never
          // compete visually with bookable slots; claimed slots keep the brand highlight
          const slotStyles: React.CSSProperties = isWaitlistSlot
            ? {
                borderColor: '#D1D5DB', // gray-300
                backgroundColor: '#F9FAFB', // gray-50
                color: '#6B7280', // gray-500
                minHeight: '48px',
              }
            : dynamicStyles;

          const handleClick = () => {
            if (isWaitlistSlot) {
              // Joining the waitlist follows the NORMAL booking flow — the
              // parent switches the usual form into waitlist mode
              onWaitlistSelect?.(slot);
              return;
            }
            handleSlotSelect(slot);
          };

          return (
            <button
              key={slotKey}
              onClick={handleClick}
              className={buttonClasses.join(" ")}
              style={slotStyles}
              disabled={isDisabled}
              onMouseEnter={(e) => {
                if (!isSelected && !isFull) {
                  e.currentTarget.style.backgroundColor = "var(--brand-color-very-light, #E8F4FA)";
                  e.currentTarget.style.borderColor = "var(--brand-color-light, #A3D3EB)";
                }
              }}
              onMouseLeave={(e) => {
                if (!isSelected && !isFull) {
                  e.currentTarget.style.backgroundColor = "white";
                  e.currentTarget.style.borderColor = "var(--brand-color-very-light, #D1E9F5)";
                }
              }}
            >
              <div className={`flex items-center w-full ${showCapacity ? 'justify-between' : 'justify-center'}`}>
                <span className="relative z-10">{timeString}</span>
                {showCapacity && (
                  <span className={`text-xs font-medium ml-2 ${
                    isClaimedSlot
                      ? ''
                      : isWaitlistSlot
                        ? 'text-gray-500'
                        : isFull
                          ? 'text-red-600'
                          : capacityInfo && capacityInfo.available === 1
                            ? 'text-orange-600'
                            : 'text-gray-500'
                  }`}
                    style={isClaimedSlot ? { color: 'var(--brand-color, #2E7AA0)' } : undefined}
                  >
                    {isClaimedSlot
                      ? t('booking:waitlist.reservedForYou', 'Reserved for you')
                      : isWaitlistSlot
                        ? `${t('booking:timeSlots.capacity.full', 'Full')} · ${t('booking:timeSlots.capacity.joinWaitlist', 'Join Waitlist')}`
                        : isFull
                          ? t('booking:timeSlots.capacity.full', 'Full')
                          : capacityInfo && capacityInfo.available === 1
                            ? t('booking:timeSlots.capacity.lastSpot', 'Last spot!')
                            : t('booking:timeSlots.capacity.spotsLeft', '{{count}} spots left', { count: capacityInfo?.available })
                    }
                  </span>
                )}
              </div>
            </button>
          );
        })
      )}
    </div>
  );
};

export default TimeSlotDisplay;
